import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Target, ArrowUpCircle, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import QualityTierBadge from './QualityTierBadge';
import QualityBar from './QualityBar';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { scoreToTier } from '../../utils/qualityHelpers';

const DashboardQualityCard = ({ qualityScore, getQualityScore, currentOrganisation }) => {
  const history = useHistory();

  useEffect(() => {
    if (currentOrganisation) getQualityScore({ breakdown: true });
  }, [currentOrganisation]);

  const distribution = useMemo(() => {
    if (!qualityScore?.byTier) return null;
    const total = (qualityScore.byTier.gold?.count || 0) +
      (qualityScore.byTier.silver?.count || 0) +
      (qualityScore.byTier.bronze?.count || 0);
    if (total === 0) return null;
    return {
      gold: ((qualityScore.byTier.gold?.count || 0) / total) * 100,
      silver: ((qualityScore.byTier.silver?.count || 0) / total) * 100,
      bronze: ((qualityScore.byTier.bronze?.count || 0) / total) * 100,
    };
  }, [qualityScore]);

  const score = qualityScore?.materialityWeightedScore || qualityScore?.overallScore || 0;
  const opportunities = qualityScore?.improvementOpportunities?.length || 0;

  if (!qualityScore) return null;

  return (
    <Card className='bg-card rounded-xl border border-border shadow-sm overflow-hidden'>
      <CardHeader className='pb-2'>
        <CardTitle className='text-sm font-medium flex items-center gap-2'>
          <Target size={16} strokeWidth={1.5} />
          Data Quality
        </CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-3'>
        <div className='flex items-center gap-3'>
          <span className='text-3xl font-bold text-foreground'>{score.toFixed(1)}</span>
          <QualityTierBadge level={Math.round(score)} />
        </div>
        {distribution && <QualityBar distribution={distribution} className='mt-1' />}
        <div className='flex items-center justify-between text-xs text-muted-foreground'>
          <span>{qualityScore.totalRecords || 0} records</span>
          {opportunities > 0 && (
            <span className='flex items-center gap-1 text-primary'>
              <ArrowUpCircle size={12} strokeWidth={1.5} />
              {opportunities} upgrade{opportunities > 1 ? 's' : ''} available
            </span>
          )}
        </div>
        <Button
          variant='outline'
          size='sm'
          className='w-full mt-1'
          onClick={() => history.push('/intelligent-import')}
        >
          <TrendingUp size={14} strokeWidth={1.5} />
          View Quality Dashboard
        </Button>
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  qualityScore: state.intelligentImport?.qualityScore,
  currentOrganisation: state.currentOrganisation,
});

const mapDispatchToProps = {
  getQualityScore: IMPORT_ACTIONS.getQualityScore,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardQualityCard);
